// Shadow-DOM rendering + base styles + theming + graceful state renderers.
// Each widget renders inside its own shadow root so host-site CSS can't bleed in (or out).
// Theming pierces the shadow boundary via CSS custom properties the host can override.
import type { ThemeMode, ThemeOptions } from './types';

const BASE_CSS = `
:host { all: initial; }
.e365 {
  --e365-primary: #1565C0;
  --e365-primary-contrast: #ffffff;
  --e365-radius: 10px;
  --e365-font: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
  --e365-border: #e2e5ea;
  --e365-muted: #6b7280;
  --e365-bg: #ffffff;
  --e365-text: #1f2430;
  --e365-surface: #ffffff;   /* inputs / unselected slots */
  --e365-skel-a: #eeeeee;    /* loading shimmer */
  --e365-skel-b: #f5f5f5;
  font-family: var(--e365-font);
  color: var(--e365-text);
  background: var(--e365-bg);
  border: 1px solid var(--e365-border);
  border-radius: var(--e365-radius);
  padding: 16px;
  box-sizing: border-box;
  line-height: 1.45;
}
.e365 *, .e365 *::before, .e365 *::after { box-sizing: border-box; }
.e365 h3 { margin: 0 0 12px; font-size: 1.15rem; font-weight: 700; }
.e365 label { display: block; font-size: .8rem; font-weight: 600; color: var(--e365-muted); margin: 10px 0 4px; }
.e365 select, .e365 input[type=date], .e365 input[type=text], .e365 input[type=email] {
  width: 100%; padding: 9px 10px; font: inherit; color: inherit;
  border: 1px solid var(--e365-border); border-radius: 8px; background: var(--e365-surface);
}
.e365 .e365-row { display: flex; gap: 10px; flex-wrap: wrap; }
.e365 .e365-row > * { flex: 1 1 160px; }
.e365 .e365-slots { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 6px; }
.e365 .e365-slot {
  padding: 8px 12px; border: 1px solid var(--e365-border); border-radius: 999px;
  background: var(--e365-surface); cursor: pointer; font: inherit; color: inherit; transition: all .15s;
}
.e365 .e365-slot:hover { border-color: var(--e365-primary); }
.e365 .e365-slot-note { display:block; font-size:11px; opacity:.7; margin-top:2px; }
.e365 .e365-slot[aria-pressed=true] .e365-slot-note { opacity:.9; }
.e365 .e365-slot[aria-pressed=true] { background: var(--e365-primary); color: var(--e365-primary-contrast); border-color: var(--e365-primary); }
.e365 .e365-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  margin-top: 14px; width: 100%; padding: 11px 16px; font: inherit; font-weight: 700;
  border: none; border-radius: var(--e365-radius); cursor: pointer;
  background: var(--e365-primary); color: var(--e365-primary-contrast);
}
.e365 .e365-btn[disabled] { opacity: .55; cursor: not-allowed; }
.e365 .e365-muted { color: var(--e365-muted); font-size: .85rem; }
.e365 .e365-alert { padding: 10px 12px; border-radius: 8px; font-size: .9rem; margin: 8px 0; }
.e365 .e365-alert.info { background: #eef4ff; color: #1d4ed8; }
.e365 .e365-alert.warn { background: #fff7ed; color: #9a3412; }
.e365 .e365-alert.error { background: #fef2f2; color: #b91c1c; }
.e365 .e365-skel { height: 14px; border-radius: 6px; background: linear-gradient(90deg, var(--e365-skel-a), var(--e365-skel-b), var(--e365-skel-a)); background-size: 200% 100%; animation: e365sk 1.2s infinite; margin: 8px 0; }
.e365 .e365-skel.lg { height: 38px; }
@keyframes e365sk { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.e365 .e365-foot { margin-top: 12px; font-size: .72rem; color: var(--e365-muted); text-align: right; }
.e365 a { color: var(--e365-primary); }
.e365 iframe { width: 100%; aspect-ratio: 16/9; border: 0; border-radius: var(--e365-radius); background:#000; }
/* Membership plan cards — responsive grid of equal-height pricing cards */
.e365 .e365-plan-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 12px; margin-top: 4px; }
.e365 .e365-plan { display: flex; flex-direction: column; border: 1px solid var(--e365-border); border-radius: var(--e365-radius); padding: 16px; background: var(--e365-surface); transition: border-color .15s, box-shadow .15s, transform .15s; }
.e365 .e365-plan:hover { border-color: var(--e365-primary); box-shadow: 0 6px 18px rgba(0,0,0,.10); transform: translateY(-2px); }
.e365 .e365-plan-name { font-weight: 700; font-size: 1.05rem; }
.e365 .e365-plan-desc { color: var(--e365-muted); font-size: .85rem; margin: 4px 0 8px; white-space: pre-wrap; }
.e365 .e365-plan-feat { list-style: none; margin: 6px 0 0; padding: 0; flex: 1 1 auto; display: flex; flex-direction: column; gap: 6px; }
.e365 .e365-plan-feat li { font-size: .85rem; display: flex; align-items: flex-start; gap: 7px; line-height: 1.3; }
.e365 .e365-plan-feat li::before { content: '✓'; color: var(--e365-primary); font-weight: 800; flex: none; }
.e365 .e365-plan-price { margin: 12px 0 2px; display: flex; align-items: baseline; gap: 3px; }
.e365 .e365-plan-amt { font-weight: 800; font-size: 1.7rem; line-height: 1.05; }
.e365 .e365-plan-per { color: var(--e365-muted); font-size: .85rem; font-weight: 600; }
/* Compare table */
.e365 .e365-cmp-wrap { overflow-x: auto; margin-top: 4px; }
.e365 .e365-cmp { border-collapse: collapse; width: 100%; min-width: 320px; }
.e365 .e365-cmp th, .e365 .e365-cmp td { text-align: left; padding: 10px 12px; border-bottom: 1px solid var(--e365-border); vertical-align: top; font-size: .9rem; }
.e365 .e365-cmp thead th { font-size: 1rem; font-weight: 700; border-bottom: 2px solid var(--e365-border); }
.e365 .e365-cmp th[scope=row] { color: var(--e365-muted); font-weight: 600; white-space: nowrap; }
.e365 .e365-cmp .e365-cmp-amt { font-weight: 800; }
.e365 .e365-cmp-save { color: #16a34a; font-weight: 700; }
.e365 .e365-cmp-feat { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
.e365 .e365-cmp-feat li { display: flex; align-items: flex-start; gap: 6px; }
.e365 .e365-cmp-feat li::before { content: '✓'; color: var(--e365-primary); font-weight: 800; flex: none; }
.e365 .e365-cmp td .e365-btn { margin-top: 4px; }
/* Watch — hourly time-frame grid */
.e365 .e365-hour-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(155px, 1fr)); gap: 10px; margin: 6px 0; }
.e365 .e365-hour { border: 1px solid var(--e365-border); border-radius: var(--e365-radius); padding: 10px; background: var(--e365-surface); }
.e365 .e365-hour-h { font-weight: 700; font-size: 1rem; }
.e365 .e365-opt { width: 100%; padding: 7px 10px; font: inherit; font-size: .85rem; cursor: pointer; border: 1px solid var(--e365-border); border-radius: 8px; background: var(--e365-surface); color: inherit; transition: border-color .15s, background .15s; }
.e365 .e365-opt:hover { border-color: var(--e365-primary); }
.e365 .e365-now { border: 2px solid var(--e365-primary); border-radius: var(--e365-radius); padding: 12px; margin: 2px 0 12px; background: var(--e365-surface); }
.e365 .e365-now-h { font-weight: 800; font-size: 1.05rem; }
/* Receipt — subtle proof-of-purchase expander under a paid item */
.e365 .e365-rcpt-btn { display: inline-flex; align-items: center; gap: 5px; background: none; border: none; padding: 3px 0; margin-top: 3px; font: inherit; font-size: .76rem; color: var(--e365-muted); cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
.e365 .e365-rcpt-btn:hover { color: var(--e365-primary); }
.e365 .e365-rcpt { margin: 5px 0 2px; padding: 9px 11px; border: 1px dashed var(--e365-border); border-radius: 8px; background: var(--e365-surface); font-size: .8rem; }
.e365 .e365-rcpt-row { display: flex; justify-content: space-between; gap: 12px; margin: 2px 0; }
.e365 .e365-rcpt-row b { font-weight: 700; }
.e365 .e365-rcpt-note { color: var(--e365-muted); font-size: .72rem; margin-top: 6px; }
/* Court picker — highlighted as the primary call-to-action on the watch landing */
.e365 .e365-pickbox { border: 2px solid var(--e365-primary); border-radius: var(--e365-radius); padding: 12px 14px; margin-top: 4px; }
.e365 .e365-pickbox > label:first-child { margin-top: 0; color: var(--e365-text); font-size: .9rem; }
.e365 .e365-pickbox select { font-size: 1rem; font-weight: 600; padding: 12px 14px; margin-top: 2px; }
`;

export interface WidgetRoot {
  host: HTMLElement;
  shadow: ShadowRoot;
  root: HTMLElement;
}

export function createRoot(mount: HTMLElement, theme: ThemeOptions): WidgetRoot {
  // Re-mounting (e.g. on return-page render): clear any prior shadow content.
  const shadow = mount.shadowRoot ?? mount.attachShadow({ mode: 'open' });
  shadow.innerHTML = '';
  const style = document.createElement('style');
  style.textContent = BASE_CSS;
  shadow.appendChild(style);
  const root = document.createElement('div');
  root.className = 'e365';
  applyTheme(root, theme);
  shadow.appendChild(root);
  return { host: mount, shadow, root };
}

// ── Pause online sales ──────────────────────────────────────────────────────────────────────────
// The venue can pause online sales (Admin → venue → Online Sales). Customers still browse, see prices
// and sign in; every book/join/buy action is replaced by the venue's message. The API refuses the
// checkout as well, so this is the friendly half, not the enforcement.

/** The venue's pause message, or null when online sales are open. */
export function salesPausedMessage(cfg: { onlineSalesPausedMessage?: string | null }): string | null {
  const m = (cfg.onlineSalesPausedMessage || '').trim();
  return m ? m : null;
}

/** Put the pause notice above a widget. It sits beside the root, so it survives the widget re-rendering. */
export function showSalesPaused(root: HTMLElement, cfg: { onlineSalesPausedMessage?: string | null }): void {
  const msg = salesPausedMessage(cfg);
  const parent = root.parentNode;
  if (!msg || !parent || (parent as ParentNode).querySelector('.e365-paused')) return;
  const note = document.createElement('div');
  note.className = 'e365-paused';
  note.setAttribute('role', 'status');
  // Fixed colours (not theme tokens): the notice sits outside the themed root and must read on light and dark.
  note.style.cssText = 'font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;'
    + 'background:#fff7e6;border:1px solid #f0c36d;color:#5b4300;border-radius:10px;padding:10px 12px;'
    + 'margin:0 0 10px;font-size:.9rem;line-height:1.4;';
  note.textContent = msg;
  parent.insertBefore(note, root);
}

/**
 * Call at the top of a book/join/buy handler. Returns true (and points the customer at the notice)
 * when online sales are paused, so the handler stops before redirecting to checkout.
 */
export function blockIfSalesPaused(cfg: { onlineSalesPausedMessage?: string | null }, from: Element): boolean {
  if (!salesPausedMessage(cfg)) return false;
  const note = (from.getRootNode() as ParentNode).querySelector<HTMLElement>('.e365-paused');
  if (note) {
    note.scrollIntoView({ behavior: 'smooth', block: 'center' });
    note.style.boxShadow = '0 0 0 3px #f0c36d';
    setTimeout(() => { note.style.boxShadow = ''; }, 1600);
  }
  return true;
}

// Color-mode presets. Applied first (when data-365-theme is set); explicit per-token props override.
// `light` mirrors the BASE_CSS defaults, so omitting the mode entirely leaves existing sites unchanged.
const PRESETS: Record<ThemeMode, Record<string, string>> = {
  light: {
    '--e365-bg': '#ffffff', '--e365-text': '#1f2430', '--e365-border': '#e2e5ea',
    '--e365-muted': '#6b7280', '--e365-surface': '#ffffff',
    '--e365-skel-a': '#eeeeee', '--e365-skel-b': '#f5f5f5',
  },
  dark: {
    '--e365-bg': '#141414', '--e365-text': '#f5f4f0', '--e365-border': '#2c2c2c',
    '--e365-muted': '#9b988f', '--e365-surface': '#1e1e1e',
    '--e365-skel-a': '#1d1d1d', '--e365-skel-b': '#262626',
  },
};

export function applyTheme(el: HTMLElement, theme: ThemeOptions): void {
  // 1) base preset (sets the surface/text/border tokens for the chosen mode)
  if (theme.mode && PRESETS[theme.mode]) {
    for (const [k, v] of Object.entries(PRESETS[theme.mode])) el.style.setProperty(k, v);
  }
  // 2) explicit per-token overrides win over the preset
  const overrides: Record<string, string | undefined> = {
    '--e365-primary': theme.primary,
    '--e365-primary-contrast': theme.primaryContrast,
    '--e365-radius': theme.radius,
    '--e365-font': theme.font,
    '--e365-bg': theme.bg,
    '--e365-text': theme.text,
    '--e365-border': theme.border,
    '--e365-muted': theme.muted,
    '--e365-surface': theme.surface,
  };
  for (const [k, v] of Object.entries(overrides)) if (v) el.style.setProperty(k, v);
}

type Attrs = Record<string, string | number | boolean | EventListener | undefined>;

/** Minimal hyperscript element builder. on* keys become event listeners. */
export function h(tag: string, attrs?: Attrs, ...children: (Node | string | null | undefined)[]): HTMLElement {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k.startsWith('on') && typeof v === 'function') {
        el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      } else if (k === 'class') {
        el.className = String(v);
      } else if (k === 'html') {
        el.innerHTML = String(v);
      } else {
        el.setAttribute(k, String(v));
      }
    }
  }
  for (const c of children) {
    if (c == null) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

export function clear(root: HTMLElement): void { root.innerHTML = ''; }

export function renderLoading(root: HTMLElement, title: string): void {
  clear(root);
  root.append(
    h('h3', undefined, title),
    h('div', { class: 'e365-skel lg' }),
    h('div', { class: 'e365-skel' }),
    h('div', { class: 'e365-skel' }),
  );
}

export function renderError(root: HTMLElement, message: string, onRetry?: () => void): void {
  clear(root);
  root.append(h('div', { class: 'e365-alert error' }, message));
  if (onRetry) root.append(h('button', { class: 'e365-btn', onClick: onRetry }, 'Try again'));
  root.append(footer());
}

export function renderEmpty(root: HTMLElement, message: string): void {
  root.append(h('div', { class: 'e365-alert info' }, message));
}

export function footer(): HTMLElement {
  return h('div', { class: 'e365-foot' }, 'Powered by 365 Sports.Online');
}

/**
 * A small "Signed in as … · Sign out" row for account widgets, so a customer can switch accounts
 * without leaving the venue's site. `email` is optional; `onSignOut` should clear auth + re-render.
 */
export function signOutRow(email: string | null | undefined, onSignOut: () => void): HTMLElement {
  const wrap = h('div', {
    style: 'display:flex; align-items:center; justify-content:center; gap:8px; flex-wrap:wrap; margin-top:12px; font-size:.82rem',
  });
  if (email) wrap.append(h('span', { class: 'e365-muted' }, `Signed in as ${email}`));
  const btn = h('button', {
    type: 'button',
    style: 'background:none; border:none; padding:0; color:var(--e365-primary); text-decoration:underline; cursor:pointer; font-size:.82rem',
  }, 'Sign out') as HTMLButtonElement;
  btn.addEventListener('click', onSignOut);
  wrap.append(btn);
  return wrap;
}

/**
 * Remove a return-flag query param (e.g. ?booking=success) from the URL after it's been read, so a
 * refresh doesn't replay the success banner. Mirrors the advertise widget's pattern.
 */
export function stripQueryParam(name: string): void {
  try {
    const u = new URL(location.href);
    if (!u.searchParams.has(name)) return;
    u.searchParams.delete(name);
    history.replaceState(null, '', u.toString());
  } catch { /* ignore */ }
}

export function money(n?: number | null): string {
  if (n == null) return '';
  return '$' + Number(n).toFixed(2);
}

// ── Sales tax ───────────────────────────────────────────────────────────────────────────────────
// The venue (merchant of record) adds sales tax at Stripe. The rate comes from the embed config
// (`salesTaxPercent`, e.g. 8.25; 0 or absent = no tax, nothing is shown). List prices stay pre-tax
// with ONE "Prices plus 8.25% sales tax" note; every book/buy/join ACTION shows the real total so
// the customer never sees $85 here and $92.01 at Stripe. The math is the server's `SalesTax.For`:
// half-up to cents ($85 at 8.25% → $7.01; $40 → $3.30).

/** Sales tax on a pre-tax amount at `pct` percent, half-up to cents. 0 when either is 0/absent. */
export function taxFor(pre: number | null | undefined, pct: number | null | undefined): number {
  const p = Number(pct) || 0;
  const a = Number(pre) || 0;
  if (a <= 0 || p <= 0) return 0;
  return Math.round((a * p / 100 + Number.EPSILON) * 100) / 100;
}

/** Pre-tax amount + its tax, to cents. */
export function totalWithTax(pre: number | null | undefined, pct: number | null | undefined): number {
  return Math.round(((Number(pre) || 0) + taxFor(pre, pct)) * 100) / 100;
}

/** "8.25%" / "8%" — a rate for sentences. */
export function pctLabel(pct: number | null | undefined): string {
  const p = Number(pct) || 0;
  return (Number.isInteger(p) ? String(p) : String(Math.round(p * 100) / 100)) + '%';
}

/** "$85.00 + $7.01 sales tax" when the rate is > 0, else just "$85.00". */
export function moneyPlusTax(pre: number | null | undefined, pct: number | null | undefined): string {
  const t = taxFor(pre, pct);
  return t > 0 ? `${money(pre)} + ${money(t)} sales tax` : money(pre);
}

/** The one note that sits beside a price list: "Prices plus 8.25% sales tax". Empty when no tax. */
export function taxNote(pct: number | null | undefined): string {
  return (Number(pct) || 0) > 0 ? `Prices plus ${pctLabel(pct)} sales tax` : '';
}

/** `taxNote` as a muted line, or null when there is no tax (so callers can `append` it blindly). */
export function taxNoteEl(pct: number | null | undefined): HTMLElement | null {
  const t = taxNote(pct);
  return t ? h('div', { class: 'e365-muted e365-taxnote', style: 'font-size:.78rem; margin:2px 0 6px' }, t) : null;
}

export interface ReceiptInfo {
  orderNumber?: string | null;
  amount?: number | null;     // amount paid, PRE-tax (the list price)
  taxAmount?: number | null;  // sales tax paid on top of `amount`; > 0 adds Subtotal / Sales tax rows
  currency?: string | null;   // e.g. "USD" (shown only when not USD)
  paidLabel?: string | null;  // pre-formatted date paid
  place?: string | null;      // "Court · Venue"
}

/**
 * A subtle "🧾 Receipt" toggle that expands an inline proof-of-purchase panel (order #, amount, date,
 * what it was for). The canonical receipt is the emailed confirmation — this just surfaces the key
 * details + order number on the page. Returns null when there's nothing worth showing.
 */
export function receipt(info: ReceiptInfo): HTMLElement | null {
  const rows: HTMLElement[] = [];
  if (info.orderNumber) rows.push(receiptRow('Order', '#' + info.orderNumber));
  if (info.amount != null && info.amount > 0) {
    const cur = info.currency && info.currency.toUpperCase() !== 'USD' ? info.currency.toUpperCase() + ' ' : '';
    const tax = Number(info.taxAmount) || 0;
    if (tax > 0) {
      // What the card was actually charged is amount + tax; the pre-tax figure is only the subtotal.
      rows.push(receiptRow('Subtotal', cur + money(info.amount)));
      rows.push(receiptRow('Sales tax', cur + money(tax)));
      rows.push(receiptRow('Paid', cur + money(Math.round((info.amount + tax) * 100) / 100) + (info.paidLabel ? ' · ' + info.paidLabel : '')));
    } else {
      rows.push(receiptRow('Paid', cur + money(info.amount) + (info.paidLabel ? ' · ' + info.paidLabel : '')));
    }
  } else if (info.paidLabel) {
    rows.push(receiptRow('Date', info.paidLabel));
  }
  if (info.place) rows.push(receiptRow('For', info.place));
  if (rows.length === 0) return null;

  const panel = h('div', { class: 'e365-rcpt', style: 'display:none' },
    ...rows,
    h('div', { class: 'e365-rcpt-note' }, 'A copy was emailed to you.'),
  );
  const btn = h('button', { class: 'e365-rcpt-btn', type: 'button', 'aria-expanded': 'false' }, '🧾 Receipt') as HTMLButtonElement;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    const open = panel.style.display === 'none';
    panel.style.display = open ? 'block' : 'none';
    btn.setAttribute('aria-expanded', String(open));
  });
  return h('div', undefined, btn, panel);
}

function receiptRow(k: string, v: string): HTMLElement {
  return h('div', { class: 'e365-rcpt-row' }, h('span', { class: 'e365-muted' }, k), h('b', undefined, v));
}
